import { useState } from "react";
import { type FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import type { VariantProps } from "~/components/article/article-speech/article-speech-inline-widget/variants/types";
import { ZIndexLayer } from "~/lib/styles";
import type { genericArticleSpeechInlineWidgetGenericArticle$key } from "~/queries/__generated__/genericArticleSpeechInlineWidgetGenericArticle.graphql";

import { ArticleSpeechInlineWidgetGenericButton } from "./button";
import type { HeadphoneIconTheme } from "./button/headphone-icon";
import { EmptyButton } from "./empty-button";
import {
  useEnableGenericInlineWidgetByUserSubscription,
  useShowGenericInlineWidgetByArticle,
} from "./hooks";
import { Container, Wrapper } from "./styles";
import { ArticleSpeechWidgetToolTip } from "./tool-tip";

type Props = {
  extendClick?: () => void;
  headphoneIconTheme?: HeadphoneIconTheme;
  isMinify?: boolean;
  reference: genericArticleSpeechInlineWidgetGenericArticle$key;
  withMinifyText?: boolean;
  zIndex?: string;
} & VariantProps;

export const ArticleSpeechInlineWidgetGeneric: FunctionComponent<Props> = ({
  className,
  extendClick,
  headphoneIconTheme = "grey",
  isMinify = false,
  reference,
  withMinifyText = true,
  zIndex = `${ZIndexLayer.Drawer - 1}`,
}) => {
  const article = useFragment(
    graphql`
      fragment genericArticleSpeechInlineWidgetGenericArticle on Article {
        entityId
        ...buttonArticleSpeechInlineWidgetGenericButtonArticle
        ...hooksShowGenericInlineWidgetByArticleArticle
        ...toolTipArticleSpeechWidgetToolTipArticle
      }
    `,
    reference,
  );
  const { isWidgetVisibleByType, scmpArticleSpeechConfigState } =
    useShowGenericInlineWidgetByArticle(article);
  const { isWidgetEnabledByUser } = useEnableGenericInlineWidgetByUserSubscription();
  const [isTooltipActive, setIsTooltipActive] = useState(false);

  const handleRenderArticleSpeechWidget = () => {
    if (scmpArticleSpeechConfigState === undefined || isWidgetEnabledByUser === undefined)
      return <EmptyButton className={className} isMinify={isMinify} />;
    else if (isWidgetVisibleByType)
      return (
        <Container className={className}>
          <ArticleSpeechWidgetToolTip
            isTooltipActive={isTooltipActive}
            reference={article}
            setIsTooltipActive={setIsTooltipActive}
          >
            <ArticleSpeechInlineWidgetGenericButton
              entityId={article.entityId}
              extendClick={extendClick}
              headphoneIconTheme={headphoneIconTheme}
              isMinify={isMinify}
              reference={article}
              setIsTooltipActive={setIsTooltipActive}
              withMinifyText={withMinifyText}
              zIndex={zIndex}
            />
          </ArticleSpeechWidgetToolTip>
        </Container>
      );
    return null;
  };

  return <Wrapper $isMinify={isMinify}>{handleRenderArticleSpeechWidget()}</Wrapper>;
};

ArticleSpeechInlineWidgetGeneric.displayName = "ArticleSpeechInlineWidgetGeneric";
